import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import EmployeeSwitcher from './EmployeeSwitcher';

export default function MobileNavigation({
  currentView,
  setCurrentView,
  currentDate,
  handleNavigate,
  onAddNew,
  onCreateSeries,
  onOpenWeeklyLog,
  showOutlookSync,
  setShowOutlookSync,
  onRemoveOutlookSyncs,
  employees = [],
  selectedEmployeeId,
  onEmployeeChange,
  employeeLoading = false,
  canWrite = true,
  isDark,
  toggleTheme,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const desktopHeaderRef = useRef(null);

  // Keep the calendar offset in sync with the real header height, so the day
  // headers are never hidden behind the fixed navigation bar.
  useEffect(() => {
    const headers = [mobileHeaderRef.current, desktopHeaderRef.current].filter(Boolean);
    if (!headers.length) return;

    const applyHeaderHeight = () => {
      // The hidden header (display: none) measures 0, so the visible one wins.
      const height = Math.max(...headers.map(header => header.getBoundingClientRect().height));
      if (height > 0) {
        document.documentElement.style.setProperty('--app-header-height', `${Math.ceil(height)}px`);
      }
    };

    applyHeaderHeight();

    const observer = new ResizeObserver(applyHeaderHeight);
    headers.forEach(header => observer.observe(header));
    window.addEventListener('resize', applyHeaderHeight);
    window.addEventListener('orientationchange', applyHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', applyHeaderHeight);
      window.removeEventListener('orientationchange', applyHeaderHeight);
    };
  }, []);

  const selectedEmployee = employees.find(employee => employee.id === selectedEmployeeId);

  const renderEmployeeSelector = ({ mobile = false } = {}) => {
    if (employees.length <= 1) return null;

    const select = (
      <EmployeeSwitcher
        employees={employees}
        selectedEmployeeId={selectedEmployeeId}
        onChange={(employeeId) => {
          onEmployeeChange(employeeId);
          if (mobile) setIsMenuOpen(false);
        }}
        loading={employeeLoading}
        className={mobile ? 'w-full' : 'w-[220px] min-w-0 shrink'}
      />
    );

    if (!mobile) return select;

    return (
      <section className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-[#36363e] dark:bg-[#202024]">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h4 className="text-sm font-bold text-gray-700 dark:text-gray-200">Employee calendar</h4>
          {selectedEmployee && !selectedEmployee.isSelf && (
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              selectedEmployee.accessLevel === 'write'
                ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
            }`}>
              {selectedEmployee.accessLevel === 'write' ? 'Read & write' : 'Read only'}
            </span>
          )}
        </div>
        {select}
      </section>
    );
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setShowMobileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Mobile Header - Fixed at top */}
      <div ref={mobileHeaderRef} className="md:hidden fixed top-0 left-0 right-0 flex items-center justify-between p-3 border-b border-gray-200 bg-white z-[2000]">
        {/* Left: Hamburger Menu */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-touch-target min-w-[60px] flex items-center justify-center"
          aria-label="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Center: Previous + Date + Next */}
        <div className="flex items-center gap-2 flex-1 justify-center">
          <button
            onClick={() => {
              const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : currentView === 'month' ? 'month' : 'week';
              const amount = 1;
              const newDate = moment(currentDate).subtract(amount, unit).toDate();
              handleNavigate(newDate);
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors min-h-touch-target min-w-touch-target flex items-center justify-center"
            aria-label="Previous"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex flex-col items-center mx-2">
            {(currentView === 'week' || currentView === 'work_week') && (
              <span className="text-xs font-medium px-2 py-0.5 bg-[#ccb778] text-white rounded-full mb-1">
                W{moment(currentDate).format('w')}
              </span>
            )}
            {currentView === 'three_day' && (
              <span className="text-xs font-medium px-2 py-0.5 bg-[#ccb778] text-white rounded-full mb-1">
                3D
              </span>
            )}
            <h2 className="text-base font-semibold text-center">
              {currentView === 'day' 
                ? moment(currentDate).format('MMM D')
                : currentView === 'three_day'
                ? (() => {
                    const start = moment(currentDate);
                    const end = moment(currentDate).add(2, 'days');
                    return start.month() === end.month() 
                      ? `${start.format('MMM D')}-${end.format('D')}`
                      : `${start.format('MMM D')} - ${end.format('MMM D')}`;
                  })()
                : moment(currentDate).format('MMM YYYY')}
            </h2>
          </div>

          <button
            onClick={() => {
              const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : currentView === 'month' ? 'month' : 'week';
              const amount = 1;
              const newDate = moment(currentDate).add(amount, unit).toDate();
              handleNavigate(newDate);
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors min-h-touch-target min-w-touch-target flex items-center justify-center"
            aria-label="Next"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Right: Add New Dropdown */}
        <div className="flex items-center gap-1">
        <div className="relative" ref={mobileDropdownRef}>
          <button
            onClick={() => canWrite && setShowMobileDropdown(!showMobileDropdown)}
            disabled={!canWrite}
            title={!canWrite ? 'Read-only calendar' : undefined}
            className="px-5 py-2 text-xs font-bold bg-[#ccb778] disabled:bg-gray-300 text-white rounded-lg min-h-touch-target min-w-[60px] flex items-center gap-1"
          >
            Add
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Mobile Dropdown Menu */}
          {showMobileDropdown && (
            <div className="absolute right-0 mt-2 w-44 border rounded-lg shadow-lg z-50" style={{ backgroundColor: isDark ? '#242428' : '#ffffff', borderColor: isDark ? '#36363e' : '#e5e7eb' }}>
              <div className="py-1">
                <button
                  onClick={() => {
                    onAddNew();
                    setShowMobileDropdown(false);
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors"
                  style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                >
                  New entry
                </button>
                <button
                  onClick={() => {
                    onCreateSeries();
                    setShowMobileDropdown(false);
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors"
                  style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                >
                  New series
                </button>
                <div style={{ height: '1px', backgroundColor: isDark ? '#36363e' : '#e5e7eb', margin: '4px 0' }} />
                <button
                  onClick={() => {
                    onOpenWeeklyLog();
                    setShowMobileDropdown(false);
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                  className="block w-full text-left px-4 py-2 text-sm transition-colors"
                  style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                >
                  Weekly Time Log
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[2100] bg-black bg-opacity-50 hamburger-menu-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="h-full w-80 overflow-y-auto bg-white text-gray-900 shadow-xl dark:bg-[#1c1c20] dark:text-gray-100" onClick={(e) => e.stopPropagation()}>
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Moalem Weitemeyer</h3>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu Content */}
            <div className="p-4 space-y-6">
              {renderEmployeeSelector({ mobile: true })}
              {/* Date Navigation */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Date Navigation</h4>
                <button
                  onClick={() => {
                    handleNavigate(new Date());
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium text-left"
                >
                  Today
                </button>
              </div>

              {/* Appearance */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Appearance</h4>
                <button
                  onClick={() => {
                    toggleTheme();
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium text-left flex items-center gap-3"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDark ? (
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                  )}
                  {isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                </button>
              </div>

              {/* View Selection */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Select view</h4>
                <div className="space-y-2">
                  {[
                    { key: 'month', label: 'Month View' },
                    { key: 'week', label: 'Week View' },
                    { key: 'work_week', label: 'Work Week' },
                    { key: 'three_day', label: '3 Days View' },
                    { key: 'day', label: 'Day View' },
                    { key: 'list', label: 'List View' }
                  ].map((view) => (
                    <button
                      key={view.key}
                      onClick={() => {
                        setCurrentView(view.key);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${
                        currentView === view.key
                          ? 'bg-white border border-[#ccb778] text-[#ccb778] font-bold'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {view.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              {canWrite && <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Add new</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onAddNew();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-[#ccb778] text-white rounded-lg font-medium hover:bg-[#b49646] transition-colors"
                  >
                    New entry                    
                  </button>
                  <button
                    onClick={() => {
                      onCreateSeries();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-[#ccb778] text-white rounded-lg font-medium hover:bg-[#b49646] transition-colors"
                  >
                    New series
                  </button>
                  <button
                    onClick={() => {
                      onOpenWeeklyLog();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-[#ccb778] text-white rounded-lg font-medium hover:bg-[#b49646] transition-colors"
                  >
                    Weekly Time Log
                  </button>
                </div>
              </div>}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation - Fixed at top */}
      <div ref={desktopHeaderRef} className="hidden md:flex fixed top-0 left-0 right-0 items-center justify-between gap-3 py-4 px-3 border-b border-gray-200 bg-white z-[2000]">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={() => handleNavigate(new Date())}
            className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
          >
            Today
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : (currentView === 'month' || currentView === 'list') ? 'month' : 'week';
                const amount = 1;
                const newDate = moment(currentDate).subtract(amount, unit).toDate();
                handleNavigate(newDate);
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => {
                const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : (currentView === 'month' || currentView === 'list') ? 'month' : 'week';
                const amount = 1;
                const newDate = moment(currentDate).add(amount, unit).toDate();
                handleNavigate(newDate);
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <h2 className="text-xl font-semibold flex min-w-0 items-center gap-2">
            <span className="truncate">
            {currentView === 'day' 
              ? moment(currentDate).format('dddd, MMMM D, YYYY')
              : currentView === 'three_day'
              ? (() => {
                  const start = moment(currentDate);
                  const end = moment(currentDate).add(2, 'days');
                  return start.month() === end.month() 
                    ? `${start.format('MMMM D')}-${end.format('D, YYYY')}`
                    : `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`;
                })()
              : moment(currentDate).format('MMMM YYYY')}
            </span>
            {(currentView === 'week' || currentView === 'work_week') && (
              <span className="shrink-0 text-sm font-normal px-2 py-0.5 bg-gray-100 rounded-full">
                Week {moment(currentDate).format('w')}
              </span>
            )}
            {currentView === 'three_day' && (
              <span className="shrink-0 text-sm font-normal px-2 py-0.5 bg-[#ccb778] text-white rounded-full">
                3 Days
              </span>
            )}
          </h2>
        </div>
        
        <div className="flex min-w-0 items-center gap-2">
          {renderEmployeeSelector()}
          <button
            className={`h-10 shrink-0 whitespace-nowrap px-4 rounded-lg border font-medium transition-colors ${
              showOutlookSync
                ? 'bg-[#ccb778] text-white border-[#ccb778] hover:bg-[#b49646]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => setShowOutlookSync(!showOutlookSync)}
          >
            Outlook Syncs
          </button>
          <button
            className="h-10 shrink-0 whitespace-nowrap px-4 rounded-lg border border-red-300 bg-white text-red-600 font-medium hover:bg-red-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onRemoveOutlookSyncs}
            disabled={!canWrite}
            title="Delete all Outlook sync events in current view"
          >
            Remove Syncs
          </button>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <div className="flex shrink-0 rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setCurrentView('month')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${
                currentView === 'month'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setCurrentView('week')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${
                currentView === 'week'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setCurrentView('work_week')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-r border-gray-300 ${
                currentView === 'work_week'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Work Week
            </button>
            <button
              onClick={() => setCurrentView('three_day')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${
                currentView === 'three_day'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              3 Days
            </button>
            <button
              onClick={() => setCurrentView('day')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${
                currentView === 'day'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => setCurrentView('list')}
              className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${
                currentView === 'list'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              List View
            </button>
          </div>
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 hover:text-gray-700"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? (
              <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>
          {/* Dropdown Button */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => canWrite && setShowDropdown(!showDropdown)}
              disabled={!canWrite}
              title={!canWrite ? 'Read-only calendar' : undefined}
              className="px-4 py-2 text-sm font-bold bg-[#ccb778] disabled:bg-gray-300 text-white rounded-lg flex items-center gap-2"
            >
              Add 
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 border rounded-lg shadow-lg z-50" style={{ backgroundColor: isDark ? '#242428' : '#ffffff', borderColor: isDark ? '#36363e' : '#e5e7eb' }}>
                <div className="py-1">
                  <button
                    onClick={() => {
                      onAddNew();
                      setShowDropdown(false);
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                    className="block w-full text-left px-4 py-2 text-sm transition-colors"
                    style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                  >
                    New entry
                  </button>
                  <button
                    onClick={() => {
                      onCreateSeries();
                      setShowDropdown(false);
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                    className="block w-full text-left px-4 py-2 text-sm transition-colors"
                    style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                  >
                    New series
                  </button>
                  <div style={{ height: '1px', backgroundColor: isDark ? '#36363e' : '#e5e7eb', margin: '4px 0' }} />
                  <button
                    onClick={() => {
                      onOpenWeeklyLog();
                      setShowDropdown(false);
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDark ? '#3a3a46' : '#f3f4f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                    className="block w-full text-left px-4 py-2 text-sm transition-colors"
                    style={{ color: isDark ? '#e5e7eb' : '#374151' }}
                  >
                    Weekly Time Log
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
